import { useState, useEffect, useCallback } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { Event, EventFormData, BusinessRuleValidation } from '../types/event';
import eventAPI from '../services/eventAPI';
import { useLocalStorage } from './useLocalStorage';
import { logError } from '../utils/errorLogger';
import { useDebouncedCallback } from './useDebouncedCallback';

interface UseEventFormOptions {
  mode: 'create' | 'edit';
  eventId?: string;
  initialData?: Partial<EventFormData>;
  autoSave?: boolean;
  autoSaveKey?: string;
  onSuccess?: (event: Event) => void;
  onError?: (error: string) => void;
}

interface UseEventFormReturn {
  // Form instance
  form: UseFormReturn<EventFormData>;
  
  // State
  loading: boolean;
  submitting: boolean;
  error: string | null;
  success: boolean;
  
  // Data
  event: Event | null;
  isDirty: boolean;
  isValid: boolean;
  
  // Validation
  validationErrors: Record<string, string>;
  businessRules: BusinessRuleValidation | null;
  
  // Actions
  submit: () => Promise<void>;
  reset: () => void;
  loadEvent: (id: string) => Promise<void>;
  validateBusinessRules: () => Promise<boolean>;
  
  // Auto-save
  saveProgress: () => void;
  clearSavedProgress: () => void;
  hasSavedProgress: boolean;
  
  // Step management (for multi-step forms)
  currentStep: number;
  setCurrentStep: (step: number) => void;
  validateStep: (step: number) => Promise<boolean>;
  goToNextStep: () => Promise<boolean>;
  goToPreviousStep: () => void;
}

const defaultFormData: EventFormData = {
  title: '',
  description: '',
  event_date: '',
  event_time: '',
  location: '',
  category_id: undefined,
  series_id: undefined,
  guest_limit: undefined,
  editable_until: '',
  message_template: '',
  link_expiry: undefined,
  // Note: access_control_flags now handled by backend
};

export const useEventForm = (options: UseEventFormOptions): UseEventFormReturn => {
  const {
    mode,
    eventId,
    initialData = {},
    autoSave = false,
    autoSaveKey = `event-form-${mode}`,
    onSuccess,
    onError,
  } = options;

  // State
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [event, setEvent] = useState<Event | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [businessRules, setBusinessRules] = useState<BusinessRuleValidation | null>(null);
  
  // Step management
  const [currentStep, setCurrentStep] = useState(0);
  
  // Auto-save storage
  const [savedProgress, setSavedProgress, clearSavedProgress] = useLocalStorage<EventFormData | null>(
    autoSaveKey,
    null
  );

  // Form setup
  const form = useForm<EventFormData>({
    defaultValues: {
      ...defaultFormData,
      ...initialData,
      ...(savedProgress || {}),
    },
    mode: 'onChange',
  });

  const { 
    handleSubmit, 
    reset: resetForm, 
    formState: { isDirty, isValid, errors },
    watch,
    trigger,
    getValues,
  } = form;

  // Watch form changes for auto-save
  const watchedValues = watch();

  // Auto-save functionality
  const saveProgress = useCallback(() => {
    if (autoSave && isDirty) {
      const currentValues = getValues();
      setSavedProgress(currentValues);
    }
  }, [autoSave, isDirty, getValues, setSavedProgress]);

  // Debounced auto-save callback
  const debouncedSaveProgress = useDebouncedCallback(saveProgress, 1000);

  // Auto-save on form changes
  useEffect(() => {
    if (autoSave) {
      debouncedSaveProgress();
    }
  }, [watchedValues, autoSave, debouncedSaveProgress]);

  // Load event data for edit mode
  const loadEvent = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const eventData = await eventAPI.getEvent(id);
      setEvent(eventData);
      
      // Convert event data to form format
      const formData: EventFormData = {
        title: eventData.title,
        description: eventData.description || '',
        event_date: eventData.event_date,
        event_time: eventData.event_time,
        location: eventData.location,
        category_id: eventData.category?.id,  // Extract ID from object
        series_id: eventData.series?.id,      // Extract ID from object
        guest_limit: eventData.guest_limit,
        editable_until: eventData.editable_until,
        message_template: eventData.message_template,
        link_expiry: eventData.link_expiry,
        // Note: access_control_flags now handled by backend
      };
      
      resetForm(formData);
      setSuccess(false);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to load event';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [resetForm, onError]);

  // Load event on mount for edit mode
  useEffect(() => {
    if (mode === 'edit' && eventId) {
      loadEvent(eventId);
    }
  }, [mode, eventId, loadEvent]);

  // Validate business rules
  const validateBusinessRules = useCallback(async (): Promise<boolean> => {
    try {
      const formData = getValues();
      const validation = await eventAPI.validateEventData(formData);
      setBusinessRules(validation);
      
      if (!validation.is_valid) {
        // Convert EventValidationError[] to Record<string, string>
        const errorRecord: Record<string, string> = {};
        validation.errors?.forEach(error => {
          errorRecord[error.field] = error.message;
        });
        setValidationErrors(errorRecord);
        return false;
      }
      
      setValidationErrors({});
      return true;
    } catch (err: any) {
      logError('Business rule validation failed', err);
      return true; // Continue if validation endpoint fails
    }
  }, [getValues]);

  // Step validation
  const validateStep = useCallback(async (step: number): Promise<boolean> => {
    // Define fields for each step
    const stepFields: Record<number, (keyof EventFormData)[]> = {
      0: ['title', 'event_date', 'event_time', 'location'], // Basic Info
      1: ['description', 'category_id'], // Event Details
      2: ['guest_limit'], // Guest Settings
      3: ['message_template'], // Communication
      4: ['editable_until', 'link_expiry', 'access_control_flags'], // Advanced
      5: [], // Review - no validation needed
    };

    const fieldsToValidate = stepFields[step] || [];
    
    if (fieldsToValidate.length > 0) {
      const isStepValid = await trigger(fieldsToValidate);
      return isStepValid;
    }
    
    return true;
  }, [trigger]);

  // Step navigation
  const goToNextStep = useCallback(async (): Promise<boolean> => {
    const isValid = await validateStep(currentStep);
    if (isValid) {
      setCurrentStep(prev => prev + 1);
      return true;
    }
    return false;
  }, [currentStep, validateStep]);

  const goToPreviousStep = useCallback(() => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  }, []);

  // Form submission
  const submit = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    setSuccess(false);
    
    try {
      // Validate business rules before submission
      const businessRulesValid = await validateBusinessRules();
      if (!businessRulesValid) {
        throw new Error('Please fix the validation errors before submitting');
      }

      const formData = getValues();
      let result: Event;

      if (mode === 'create') {
        result = await eventAPI.createEvent(formData);
      } else if (mode === 'edit' && eventId) {
        result = await eventAPI.updateEvent(eventId, formData);
      } else {
        throw new Error('Invalid form mode or missing event ID');
      }

      setEvent(result);
      setSuccess(true);
      
      // Clear saved progress on successful submission
      if (autoSave) {
        clearSavedProgress();
      }
      
      onSuccess?.(result);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || `Failed to ${mode} event`;
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setSubmitting(false);
    }
  }, [mode, eventId, getValues, validateBusinessRules, autoSave, clearSavedProgress, onSuccess, onError]);

  // Reset form
  const reset = useCallback(() => {
    resetForm(mode === 'edit' && event ? {
      title: event.title,
      description: event.description || '',
      event_date: event.event_date,
      event_time: event.event_time,
      location: event.location,
      category_id: event.category?.id,      // Extract ID from object
      series_id: event.series?.id,          // Extract ID from object
      guest_limit: event.guest_limit,
      editable_until: event.editable_until,
      message_template: event.message_template,
      link_expiry: event.link_expiry,
      // Note: access_control_flags now handled by backend
    } : defaultFormData);
    
    setError(null);
    setSuccess(false);
    setValidationErrors({});
    setBusinessRules(null);
    
    if (autoSave) {
      clearSavedProgress();
    }
  }, [mode, event, resetForm, autoSave, clearSavedProgress]);

  // Convert form errors to validation errors
  useEffect(() => {
    const formErrors: Record<string, string> = {};
    Object.entries(errors).forEach(([field, error]) => {
      if (error?.message && typeof error.message === 'string') {
        formErrors[field] = error.message;
      }
    });
    setValidationErrors(prev => ({ ...prev, ...formErrors }));
  }, [errors]);

  return {
    // Form instance
    form,
    
    // State
    loading,
    submitting,
    error,
    success,
    
    // Data
    event,
    isDirty,
    isValid,
    
    // Validation
    validationErrors,
    businessRules,
    
    // Actions
    submit: handleSubmit(submit),
    reset,
    loadEvent,
    validateBusinessRules,
    
    // Auto-save
    saveProgress,
    clearSavedProgress,
    hasSavedProgress: !!savedProgress,
    
    // Step management
    currentStep,
    setCurrentStep,
    validateStep,
    goToNextStep,
    goToPreviousStep,
  };
};

export default useEventForm;
